/** Home (design: dashboard-v7 #homeView), with real data. */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { FEATURES } from '~/config/mode'
import { gallery, inbox, type GalleryImage } from '~/api/endpoints'
import { seasonIcons } from '~/content/content'
import { IS_MOCK } from '~/config/mode'
import { useMailingAddress } from '@/features/mailing/useMailingAddress'
import { EnvelopeModal } from '@/features/mailing/EnvelopeModal'
import { useAccessLevel } from '@/hooks/useAccessLevel'
import { ButtonRow, HomeVariant, VariantPicker, useHomeVariant } from './home/HomeVariants'
import { Icon, shortTime, useShowModal, useToast } from '~/app/ui'
import { usePin } from '~/app/pin'
import { useUnread } from '~/app/useUnread'
import { Onboarding } from '~/app/Onboarding'
import { SUPPORT_EMAIL } from '@/config/brandConfig'
import { nick, noteSignIn, lastSignInText } from '~/app/device'

export function Home() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { go } = usePin()
  const toast = useToast()
  const { loading, conversation, latest, unread } = useUnread()
  const [tiles, setTiles] = useState<GalleryImage[]>([])
  const uid = user?.id || ''
  const [nickname, setNickname] = useState(() => (FEATURES.nickname ? nick.get(uid) : ''))
  const [nickOpen, setNickOpen] = useState<'first' | 'edit' | null>(null)
  const firstName = (user as any)?.firstName || (user as any)?.first_name || ''
  const name = nickname.trim() || firstName || 'there'
  const [lastSeen] = useState(() => (uid ? noteSignIn(uid) : null))

  useEffect(() => { gallery.images(3, 0).then((r) => setTiles(r.images.slice(0, 3))) }, [])
  // One-time "What should Caryn call you?" - only once the first-login steps are done (the fan has a name)
  useEffect(() => {
    if (FEATURES.nickname && uid && firstName && !nick.asked(uid) && !new URLSearchParams(location.search).has('nonick')) {
      const t = setTimeout(() => setNickOpen('first'), 900)
      return () => clearTimeout(t)
    }
  }, [uid, firstName])

  const words = (latest?.content || '').trim().split(/\s+/).filter(Boolean)
  const caryn = conversation?.creator
  const variant = useHomeVariant()
  const [inboxNew, setInboxNew] = useState(0)
  useEffect(() => { inbox.list().then((m) => setInboxNew(m.filter((x) => !x.openedAt).length)).catch(() => {}) }, [])
  const { hasAddress, isLoading: mailLoading, refresh: refreshMailing } = useMailingAddress()
  const [envelope, setEnvelope] = useState(false)
  const access = useAccessLevel()
  const expired = !access.loading && !access.hasAccess && !access.requiresOnboarding
  const openTexts = () => go('/text')

  return (
    <main className="wrap" id="main" tabIndex={-1}>
      <Onboarding />
      <section className="greeting" aria-labelledby="hello">
        <div className="decor" aria-hidden="true">
          {seasonIcons().map((id, i) => <span key={id + i} className={`deco deco-${i + 1}`}><svg><use href={`#${id}`} /></svg></span>)}
        </div>
        <p className="eyebrow">Your private lounge</p>
        <h1 id="hello">
          Hello, <em>{name}</em>!
          {FEATURES.nickname && <button className="pencil" type="button" aria-label="Edit nickname" onClick={() => setNickOpen('edit')}><Icon id="i-pen" /></button>}
        </h1>
        {/* owner 11-04: subline removed ("A little corner of my world, kept just for you.") */}
        <div className="flourish" aria-hidden="true"><Icon id="i-heart" /></div>
      </section>

      {variant !== 'design' && variant !== 'tiles' ? (
        <HomeVariant variant={variant} unread={unread} latest={latest?.content || ''} loading={loading} photos={tiles}
          inboxNew={inboxNew} hasAddress={hasAddress || mailLoading} onMailbox={() => setEnvelope(true)} expired={expired} go={go} navigate={navigate} />
      ) : (
      <section className={`grid v2-no-note${variant === 'tiles' ? ' v2-compact' : ''}`} aria-label="Dashboard">
        <article className="card conversation on-dark" aria-labelledby="convTitle" onClick={(e) => { if (!(e.target as HTMLElement).closest('button, a')) openTexts() }}>
          <div className="conv-head">
            <div className="avatar avatar-photo" aria-hidden="true">
              {caryn?.avatar_url ? <img src={caryn.avatar_url} alt="" width={230} height={230} /> : <span>C</span>}
            </div>
            <div className="conv-title">
              <h2 id="convTitle">Your texts</h2>
              <p>with {caryn?.username || 'Caryn'}{' '}
                {unread > 0 && <span className="pill unread-pill"><Icon id="i-heart" className="icon unread-heart" /><span>{unread} unread</span></span>}
              </p>
            </div>
          </div>
          <div className="last-message">
            <p className="eyebrow">Latest message{latest ? ` · ${shortTime(latest.created_at)}` : ''}</p>
            {loading ? <p>&nbsp;</p> : words.length ? (
              <p>
                <span>{words.slice(0, 4).join(' ')}</span>{' '}
                {words.length > 4 && <span className="last-blur" aria-hidden="true">{words.slice(4).join(' ')}</span>}
                {words.length > 4 && <span className="sr-only"> …the rest is waiting for you.</span>}
              </p>
            ) : <p>Say hi, I’d love to hear from you.</p>}
          </div>
          <div className="conv-foot"><Icon id="i-chat" /><span>Private, just between us.</span></div>
          <button className="btn btn-light btn-cta" type="button" onClick={openTexts}>
            <em>Continue our conversation</em> {FEATURES.pinGate && <Icon id="i-lock" />}
          </button>
        </article>

        <button className="card card-link gallery" type="button" onClick={() => navigate('/gallery')}>
          <div className="row"><Icon id="i-image" /><Icon id="i-arrow" className="icon arrow" /></div>
          <h3>The gallery</h3>
          <p className="desc">New moments to explore</p>
          <div className="tiles" aria-hidden="true">
            {tiles.map((t) => <span className="tile" key={t.id}>{t.urls.thumbnail && <img src={t.urls.thumbnail} alt="" loading="lazy" decoding="async" width={640} height={960} />}</span>)}
          </div>
        </button>

        {variant === 'design' && (
        <button className="card card-link vault-link" type="button" onClick={() => go('/vault')}>
          <VaultDoor />
          <span className="vault-copy">
            <span className="vault-title">Your Vault</span>
            <span className="vault-sub"><Icon id="i-lock" className="icon lock" />{FEATURES.pinGate ? 'Enter your PIN to access' : 'Only you can see it'}</span>
          </span>
        </button>
        )}
      </section>
      )}
      {variant === 'tiles' && <ButtonRow inboxNew={inboxNew} go={go} navigate={navigate} />}

      <footer className="foot v2-foot-moved">
        <span>{user?.email ? <>Logged in as {user.email}<br /></> : null}<span className="foot-secure"><Icon id="i-lock" />Confidential · Secure{FEATURES.lastSignIn ? ` · ${lastSignInText(lastSeen)}` : ''}</span></span>
        <a className="link-btn" href={`mailto:${SUPPORT_EMAIL}`}>Support</a>
      </footer>
      {IS_MOCK && (() => { try { return new URLSearchParams(location.search).has('home') || !!localStorage.getItem('cw-home-variant') } catch { return false } })() && <VariantPicker current={variant} />}
      {envelope && <div className="tw-scope"><EnvelopeModal isOpen onClose={() => setEnvelope(false)} onAddressSaved={refreshMailing} /></div>}

      {nickOpen && (
        <NickDialog first={nickOpen === 'first'} value={nickname}
          onClose={() => { nick.markAsked(uid); setNickOpen(null) }}
          onSave={(v) => { setNickname(v); nick.set(uid, v); nick.markAsked(uid); setNickOpen(null); window.dispatchEvent(new Event('cw-nick-changed')); toast('Saved on this device (test version).') }} />
      )}
    </main>
  )
}

function VaultDoor() {
  return (
    <svg className="vault-door" viewBox="0 0 64 64" aria-hidden="true">
      <rect className="vd-body" x="3" y="3" width="58" height="58" rx="13" />
      <circle className="vd-bolt" cx="11" cy="11" r="2" /><circle className="vd-bolt" cx="53" cy="11" r="2" /><circle className="vd-bolt" cx="11" cy="53" r="2" /><circle className="vd-bolt" cx="53" cy="53" r="2" />
      <circle className="vd-ring" cx="32" cy="32" r="21" />
      <circle className="vd-ring2" cx="32" cy="32" r="16" />
      <g className="vd-dial"><path d="M32 21v22M21 32h22M24.2 24.2l15.6 15.6M39.8 24.2L24.2 39.8" /><circle className="vd-hub" cx="32" cy="32" r="5.5" /></g>
    </svg>
  )
}

function NickDialog({ first, value, onSave, onClose }: { first: boolean; value: string; onSave: (v: string) => void; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)
  const [v, setV] = useState(first ? '' : value)
  useShowModal(ref)
  return (
    <dialog className="nick" ref={ref} aria-labelledby="nickTitle" onClose={onClose}>
      <h2 id="nickTitle">{first ? 'What should Caryn call you?' : 'Change your nickname'}</h2>
      <p>Test version: saved on this device for now. Soon Caryn will see it next to your name in your texts.</p>
      <form method="dialog" onSubmit={(e) => { e.preventDefault(); onSave(v.trim()) }}>
        <label className="sr-only" htmlFor="nickInput">Nickname</label>
        <input id="nickInput" maxLength={24} autoComplete="off" placeholder="Your nickname" value={v} onChange={(e) => setV(e.target.value)} autoFocus />
        <button className="btn btn-sm" type="submit">Save</button>
        <button className="link-btn" type="button" style={{ justifyContent: 'center' }} onClick={() => ref.current?.close()}>{first ? 'Maybe later' : 'Cancel'}</button>
      </form>
    </dialog>
  )
}
